/**
 * Calculation Service
 * 
 * Contains all calculation functions for the wizard summary.
 * All functions receive the config object from BulkConfiguration or CutConfiguration.
 */

/**
 * Parse a value to float, returning 0 if invalid
 */
const toNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Calculate Body Weight Target
 * Formula: nightWeight * (1 - (currentFatPercentage/100)) / (1 - (targetFatPercentage/100)) + leanMassGain
 */
export const calculateBodyWeightTarget = (config) => {
  const nightWeight = toNumber(config.nightWeight);
  const currentFatPercentage = toNumber(config.currentFatPercentage);
  const targetFatPercentage = toNumber(config.targetFatPercentage);
  const leanMassGain = toNumber(config.leanMassGain);

  if (targetFatPercentage >= 100) return 0;

  const result = (nightWeight * (1 - currentFatPercentage / 100)) / (1 - targetFatPercentage / 100) + leanMassGain;
  return result;
};

/**
 * Calculate Lean Mass Ratio
 * Formula: leanMassGain / time
 */
export const calculateLeanMassRatio = (config) => {
  const leanMassGain = toNumber(config.leanMassGain);
  const time = toNumber(config.time);

  if (time === 0) return 0;

  return leanMassGain / time;
};

/**
 * Calculate Current Body Fat (kg)
 * Formula: nightWeight * currentFatPercentage / 100
 */
export const calculateCurrentBodyFat = (config) => {
  const nightWeight = toNumber(config.nightWeight);
  const currentFatPercentage = toNumber(config.currentFatPercentage);

  return (nightWeight * currentFatPercentage) / 100;
};

/**
 * Calculate Current Lean Mass (kg)
 * Formula: nightWeight - Current Body Fat (kg)
 */
export const calculateCurrentLeanMass = (config) => {
  const nightWeight = toNumber(config.nightWeight);
  const currentBodyFat = calculateCurrentBodyFat(config);

  return nightWeight - currentBodyFat;
};

/**
 * Calculate Lean Mass Target
 * Formula: Current Lean Mass (kg) + leanMassGain
 */
export const calculateLeanMassTarget = (config) => {
  const currentLeanMass = calculateCurrentLeanMass(config);
  const leanMassGain = toNumber(config.leanMassGain);

  return currentLeanMass + leanMassGain;
};

/**
 * Calculate Body Fat End
 * Formula: ((nightWeight + leanMassGain) * targetFatPercentage) / 100
 */
export const calculateBodyFatEnd = (config) => {
  const nightWeight = toNumber(config.nightWeight);
  const leanMassGain = toNumber(config.leanMassGain);
  const targetFatPercentage = toNumber(config.targetFatPercentage);

  return ((nightWeight + leanMassGain) * targetFatPercentage) / 100;
};

/**
 * Calculate Fat Difference
 * Formula: Current Body Fat (kg) - Body Fat End
 */
export const calculateFatDifference = (config) => {
  const currentBodyFat = calculateCurrentBodyFat(config);
  const bodyFatEnd = calculateBodyFatEnd(config);

  return currentBodyFat - bodyFatEnd;
};

/**
 * Calculate Fat Difference Ratio
 * Formula: Fat Difference / time
 */
export const calculateFatDifferenceRatio = (config) => {
  const fatDifference = calculateFatDifference(config);
  const time = toNumber(config.time);

  if (time === 0) return 0;

  return fatDifference / time;
};

/**
 * Calculate Fat Percentage Difference
 * Formula: currentFatPercentage - targetFatPercentage
 */
export const calculateFatPercentageDifference = (config) => {
  const currentFatPercentage = toNumber(config.currentFatPercentage);
  const targetFatPercentage = toNumber(config.targetFatPercentage);

  return currentFatPercentage - targetFatPercentage;
};

/**
 * Calculate Total Caloric Deficit
 * Formula: (-1 * Fat Difference) * 1000 * 7
 */
export const calculateTotalCaloricDeficit = (config) => {
  const fatDifference = calculateFatDifference(config);

  return -1 * fatDifference * 1000 * 7;
};

/**
 * Calculate Weight Difference Rhythm
 * Formula: -((Fat Difference Ratio - Lean Mass Ratio) * 1000)
 */
export const calculateWeightDifferenceRhythm = (config) => {
  const fatDifferenceRatio = calculateFatDifferenceRatio(config);
  const leanMassRatio = calculateLeanMassRatio(config);

  return -((fatDifferenceRatio - leanMassRatio) * 1000);
};

/**
 * Calculate First Param Ratio (Skinfold 1)
 * Formula: (skinfold1current - skinfold1target) / time
 */
export const calculateFirstParamRatio = (config) => {
  const time = toNumber(config.time);
  if (time === 0 || !config.skinFolds?.[0]) return 0;

  const current = toNumber(config.skinFolds[0].current);
  const target = toNumber(config.skinFolds[0].target);

  return (current - target) / time;
};

/**
 * Calculate Second Param Ratio (Skinfold 2)
 * Formula: (skinfold2current - skinfold2target) / time
 */
export const calculateSecondParamRatio = (config) => {
  const time = toNumber(config.time);
  if (time === 0 || !config.skinFolds?.[1]) return 0;

  const current = toNumber(config.skinFolds[1].current);
  const target = toNumber(config.skinFolds[1].target);

  return (current - target) / time;
};

/**
 * Calculate Third Param Ratio (Perimeter 1)
 * Formula: (perimeter1current - perimeter1target) / time
 */
export const calculateThirdParamRatio = (config) => {
  const time = toNumber(config.time);
  if (time === 0 || !config.perimeters?.[0]) return 0;

  const current = toNumber(config.perimeters[0].current);
  const target = toNumber(config.perimeters[0].target);

  return (current - target) / time;
};

/**
 * Calculate Fourth Param Ratio (Perimeter 2)
 * Formula: (perimeter2current - perimeter2target) / time
 */
export const calculateFourthParamRatio = (config) => {
  const time = toNumber(config.time);
  if (time === 0 || !config.perimeters?.[1]) return 0;

  const current = toNumber(config.perimeters[1].current);
  const target = toNumber(config.perimeters[1].target);

  return (current - target) / time;
};

/**
 * Skinfold Perimeter Rhythm Coefficient
 * Constant value = 1
 */
export const getSkinfoldPerimeterRhythmCoefficient = () => {
  return 1;
};

/**
 * Calculate all parameters at once
 * Returns an object with all calculated values
 */
export const calculateAllParameters = (config) => {
  return {
    bodyWeightTarget: calculateBodyWeightTarget(config),
    leanMassRatio: calculateLeanMassRatio(config),
    currentBodyFat: calculateCurrentBodyFat(config),
    currentLeanMass: calculateCurrentLeanMass(config),
    leanMassTarget: calculateLeanMassTarget(config),
    bodyFatEnd: calculateBodyFatEnd(config),
    fatDifference: calculateFatDifference(config),
    fatDifferenceRatio: calculateFatDifferenceRatio(config),
    fatPercentageDifference: calculateFatPercentageDifference(config),
    totalCaloricDeficit: calculateTotalCaloricDeficit(config),
    weightDifferenceRhythm: calculateWeightDifferenceRhythm(config),
    firstParamRatio: calculateFirstParamRatio(config),
    secondParamRatio: calculateSecondParamRatio(config),
    thirdParamRatio: calculateThirdParamRatio(config),
    fourthParamRatio: calculateFourthParamRatio(config),
    skinfoldPerimeterRhythmCoefficient: getSkinfoldPerimeterRhythmCoefficient(),
  };
};

// ============================================
// CALENDAR LINE CALCULATIONS
// ============================================

/**
 * Calculate the first line (L0) of the calendar
 * @param {object} config - The configuration object
 * @param {object} calculatedParams - Pre-calculated parameters
 * @returns {object} Line 0 values
 */
export const calculateLineZero = (config, calculatedParams) => {
  const coefficient = getSkinfoldPerimeterRhythmCoefficient();
  
  const skinfold1Current = toNumber(config.skinFolds?.[0]?.current);
  const skinfold2Current = toNumber(config.skinFolds?.[1]?.current);
  const perimeter1Current = toNumber(config.perimeters?.[0]?.current);
  const perimeter2Current = toNumber(config.perimeters?.[1]?.current);
  const morningWeight = toNumber(config.morningWeight);
  const nightWeight = toNumber(config.nightWeight);
  const currentFatPercentage = toNumber(config.currentFatPercentage);

  return {
    firstParam: skinfold1Current - (calculatedParams.firstParamRatio * coefficient),
    secondParam: skinfold2Current - (calculatedParams.secondParamRatio * coefficient),
    thirdParam: perimeter1Current - (calculatedParams.thirdParamRatio * coefficient),
    fourthParam: perimeter2Current - (calculatedParams.fourthParamRatio * coefficient),
    fatPercentage: currentFatPercentage - calculatedParams.fatDifferenceRatio,
    morningWeight: morningWeight - calculatedParams.fatDifferenceRatio + calculatedParams.leanMassRatio,
    nightWeight: nightWeight - calculatedParams.fatDifferenceRatio + calculatedParams.leanMassRatio,
  };
};

/**
 * Calculate the next line based on the previous line
 * @param {object} previousLine - The previous line values
 * @param {object} calculatedParams - Pre-calculated parameters
 * @returns {object} Next line values
 */
export const calculateNextLine = (previousLine, calculatedParams) => {
  const coefficient = getSkinfoldPerimeterRhythmCoefficient();

  return {
    firstParam: previousLine.firstParam - (calculatedParams.firstParamRatio * coefficient),
    secondParam: previousLine.secondParam - (calculatedParams.secondParamRatio * coefficient),
    thirdParam: previousLine.thirdParam - (calculatedParams.thirdParamRatio * coefficient),
    fourthParam: previousLine.fourthParam - (calculatedParams.fourthParamRatio * coefficient),
    fatPercentage: previousLine.fatPercentage - calculatedParams.fatDifferenceRatio,
    morningWeight: previousLine.morningWeight - calculatedParams.fatDifferenceRatio + calculatedParams.leanMassRatio,
    nightWeight: previousLine.nightWeight - calculatedParams.fatDifferenceRatio + calculatedParams.leanMassRatio,
  };
};

/**
 * Generate all calendar lines based on the time period with real dates
 * @param {object} config - The configuration object
 * @param {object} calculatedParams - Pre-calculated parameters
 * @returns {Array} Array of line objects with their values and dates
 */
export const generateCalendarLines = (config, calculatedParams) => {
  const time = toNumber(config.time);
  const rhythm = config.rhythm || "weekly";
  if (time <= 0) return [];

  const lines = [];
  const today = new Date();
  let currentDate;
  
  // Initialize start date based on rhythm
  if (rhythm === "weekly") {
    // Start from Monday of current week
    currentDate = getMondayOfWeek(today);
  } else if (rhythm === "monthly") {
    // Start from first day of current month
    currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
  } else {
    // Daily: start from today
    currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
  
  // Calculate L0
  const lineZero = calculateLineZero(config, calculatedParams);
  lines.push({
    index: 0,
    date: new Date(currentDate.getTime()),
    ...lineZero,
  });

  // Calculate remaining lines (L1 to L(time-1))
  for (let i = 1; i < time; i++) {
    // Advance date based on rhythm
    if (rhythm === "daily") {
      currentDate.setDate(currentDate.getDate() + 1);
    } else if (rhythm === "weekly") {
      currentDate.setDate(currentDate.getDate() + 7);
    } else if (rhythm === "monthly") {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    const previousLine = lines[i - 1];
    const nextLine = calculateNextLine(previousLine, calculatedParams);
    lines.push({
      index: i,
      date: new Date(currentDate.getTime()),
      ...nextLine,
    });
  }

  return lines;
};

/**
 * Get the Monday of the ISO week containing the given date
 * @param {Date} date - Any date
 * @returns {Date} Monday of that week
 */
const getMondayOfWeek = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  // day: 0=Sunday, 1=Monday, ..., 6=Saturday
  // We need to go back to Monday
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

/**
 * Get the number of weeks in a given month
 * @param {number} year - The year
 * @param {number} month - The month (0-indexed)
 * @returns {number} Number of weeks
 */
export const getWeeksInMonth = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const firstDayOfWeek = firstDay.getDay();
  
  // Calculate number of weeks (a week counts if it has at least one day in this month)
  return Math.ceil((daysInMonth + firstDayOfWeek) / 7);
};

/**
 * Get the number of days in a given month
 * @param {number} year - The year
 * @param {number} month - The month (0-indexed)
 * @returns {number} Number of days
 */
export const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get the ISO week number of the year
 * @param {Date} date - The date (should be a Monday)
 * @returns {number} Week number (1-53)
 */
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * Format date based on rhythm
 * @param {Date} date - The date to format (Monday for weekly rhythm)
 * @param {string} rhythm - "daily", "weekly", or "monthly"
 * @returns {string} Formatted date string
 */
const formatDateByRhythm = (date, rhythm) => {
  const options = { day: 'numeric', month: 'short' };
  
  if (rhythm === "daily") {
    // Show: "25 Dec"
    return date.toLocaleDateString('en-US', options);
  } else if (rhythm === "weekly") {
    // Show: "Week 1 (Dec 29 - Jan 4)"
    // date is already Monday, Sunday is +6 days
    const weekNumber = getWeekNumber(date);
    const sunday = new Date(date.getTime());
    sunday.setDate(sunday.getDate() + 6);
    const startStr = date.toLocaleDateString('en-US', options);
    const endStr = sunday.toLocaleDateString('en-US', options);
    return `Week ${weekNumber} (${startStr} - ${endStr})`;
  } else if (rhythm === "monthly") {
    // Show: "December 2024"
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  return date.toLocaleDateString('en-US', options);
};

/**
 * Organize calendar lines by month based on their actual dates
 * @param {Array} lines - Array of calculated lines with dates
 * @param {string} rhythm - "daily", "weekly", or "monthly"
 * @returns {Array} Array of month objects containing their lines
 */
export const organizeLinesByMonth = (lines, rhythm) => {
  if (!lines || lines.length === 0) return [];

  const monthsMap = new Map();
  
  lines.forEach((line) => {
    const date = line.date;
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthKey = `${year}-${month}`;
    const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    if (!monthsMap.has(monthKey)) {
      monthsMap.set(monthKey, {
        name: monthName,
        year,
        month,
        lines: [],
      });
    }
    
    monthsMap.get(monthKey).lines.push({
      ...line,
      periodLabel: formatDateByRhythm(date, rhythm),
    });
  });
  
  // Convert map to array and sort by date
  return Array.from(monthsMap.values()).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
};

/**
 * Get the label for a period (day, week, or month)
 * @param {string} rhythm - "daily", "weekly", or "monthly"
 * @param {number} index - Index within the month
 * @param {Date} monthDate - Date of the month
 * @returns {string} Period label
 */
const getPeriodLabel = (rhythm, index, monthDate) => {
  if (rhythm === "monthly") {
    return "Month";
  } else if (rhythm === "weekly") {
    return `Week ${index + 1}`;
  } else if (rhythm === "daily") {
    return `Day ${index + 1}`;
  }
  return "";
};